import axios from 'axios';
import { API_BASE_URL } from '../baseUrl/apiConfig';
import webhookClient from './urlBase';

// const BASE_URL = window.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
const BASE_URL = `${API_BASE_URL}/api`;

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 60000, // 60 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    
  }
});

const updateLoginStatus = async (appName, statusCode, statusMessage) => {
  try {
    const response = await webhookClient.post('/update-login-status', {
      app_name: appName,
      status_code: statusCode,
      status_message: statusMessage
    });
    return response.data;
  } catch (error) {
    console.error('Error updating login status:', error);
    // Don't throw error here, just log it
    // We don't want to fail the login if status update fails
    return null;
  }
};

// Check login status manually via webhook endpoint
const checkLoginStatus = async (appName) => {
  try {
    const response = await webhookClient.post('/check-login-status', {
      app_name: appName
    });
    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Status check completed'
    };
  } catch (error) {
    console.error('Error checking login status:', error);
    throw {
      success: false,
      status: error.response?.status || 500,
      message: error.response?.data?.message || error.message || 'Failed to check login status'
    };
  }
};

const loginToProlamudi = async (credentials) => {
  try {
    const response = await apiClient.post('/v2/prolamudi/login', credentials);

    // If login is successful, update the login status
    if (response.data && response.data.success) {
      await updateLoginStatus(
        'prolamudi',
        200,
        response.data.message || 'Successfully logged in!'
      );
    }

    return response.data;
  } catch (error) {
    console.error('Error logging into Prolamudi:', error);

    // Update status with error
    const statusCode = error.response?.status || 500;
    const statusMessage = error.response?.data?.message || error.message || 'Login failed';
    await updateLoginStatus('prolamudi', statusCode, statusMessage);

    throw error;
  }
};

const loginToCarousell = async (credentials) => {
  try {
    const response = await apiClient.post('/v2/carousell/login', credentials);

    // If login is successful, update the login status
    if (response.data && response.data.success) {
      await updateLoginStatus(
        'carousell',
        200,
        response.data.message || 'Successfully logged in!'
      );
    }

    return response.data;
  } catch (error) {
    console.error('Error logging into Carousell:', error);

    // Update status with error
    const statusCode = error.response?.status || 500;
    const statusMessage = error.response?.data?.message || error.message || 'Login failed';
    await updateLoginStatus('carousell', statusCode, statusMessage);

    throw error;
  }
};

const loginToApp = async (appName, credentials) => {
  const appNameLower = appName.toLowerCase();

  switch (appNameLower) {
    case 'prolamudi':
      return loginToProlamudi(credentials);
    case 'carousell':
      return loginToCarousell(credentials);
    default:
      throw new Error(`Unknown app: ${appName}`);
  }
};

// Get all login statuses
const getAllLoginStatuses = async () => {
  try {
    const response = await webhookClient.get('/get-all-automation-login-status');

    // Handle the nested data structure
    // The response is an array with an object containing a 'data' array
    if (Array.isArray(response.data) && response.data.length > 0 && response.data[0].data) {
      return response.data[0].data;
    }

    // Fallback for different response structures
    if (response.data && response.data.data) {
      return response.data.data;
    }

    // If it's already the correct format, return as is
    return response.data;
  } catch (error) {
    console.error('Error fetching all login statuses:', error);
    throw error;
  }
};

// Create a new app login
const createAppLogin = async (appName) => {
  try {
    const response = await webhookClient.post('/create-app-login', {
      app_name: appName
    });
    return {
      success: true,
      message: response.data.message || `App Name: ${appName} is successfully created!`
    };
  } catch (error) {
    console.error('Error creating app login:', error);

    // Handle 422 validation error (app name already exists)
    if (error.response && error.response.status === 422) {
      throw {
        success: false,
        status: 422,
        message: error.response.data.message || `App name: ${appName} is already used.`
      };
    }

    // Handle other errors
    throw {
      success: false,
      status: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to create app login'
    };
  }
};

// Delete an app login
const deleteAppLogin = async (id) => {
  try {
    const response = await webhookClient.get(`/delete_app_login?id=${id}`);
    return {
      success: true,
      message: response.data.message || 'App login deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting app login:', error);
    throw {
      success: false,
      status: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to delete app login'
    };
  }
};

const LoginActivityApi = {
  loginToProlamudi,
  loginToCarousell,
  loginToApp,
  updateLoginStatus,
  checkLoginStatus,
  getAllLoginStatuses,
  createAppLogin,
  deleteAppLogin
};

export default LoginActivityApi;
